import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdminStage,
  CreateStageInput,
  OpportunityStatus,
  ReorderStagesInput,
  Setting,
} from "@salesboard/contracts";
import { useT } from "@/i18n";
import type { ManagerColor } from "@salesboard/contracts";
import { api } from "@/lib/api";
import { reportMutationError, reportMutationSuccess } from "@/lib/mutations";

/**
 * Board configuration.
 *
 * Every mutation returns the **whole** list, because these operations move rows other than
 * the one edited: a type change relocates a stage into its block and renumbers the rest, and
 * a reorder moves everything. Writing the response straight into the cache is what stops the
 * screen from briefly showing an order the server has already replaced.
 */
const STAGES = ["stages"] as const;
const SETTINGS = ["settings"] as const;

export function useAdminStages() {
  return useQuery({ queryKey: STAGES, queryFn: () => api.get<AdminStage[]>("/stages") });
}

function useStageMutation<TInput>(
  run: (input: TInput) => Promise<AdminStage[]>,
  success: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async (stages) => {
      queryClient.setQueryData(STAGES, stages);
      await queryClient.invalidateQueries({ queryKey: ["stages", "history"] });
      // The board renders these rows and the filters label with them, so a change here has
      // to reach every opportunity query too.
      await queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      reportMutationSuccess(success);
    },
    onError: (error: unknown) => reportMutationError(error),
  });
}

export function useCreateStage() {
  const t = useT();
  return useStageMutation<CreateStageInput>(
    (input) => api.post<AdminStage[]>("/stages", input),
    t("toast.stageAdded"),
  );
}

export function useRenameStage() {
  const t = useT();
  return useStageMutation<{ id: string; name: string; version: number }>(
    ({ id, ...body }) => api.patch<AdminStage[]>(`/stages/${id}/name`, body),
    t("toast.stageRenamed"),
  );
}

export function useSetStageType() {
  const t = useT();
  return useStageMutation<{ id: string; closedType: OpportunityStatus; version: number }>(
    ({ id, ...body }) => api.patch<AdminStage[]>(`/stages/${id}/type`, body),
    t("toast.stageTypeChanged"),
  );
}

export function useSetStageColor() {
  const t = useT();
  return useStageMutation<{ id: string; color: ManagerColor | null; version: number }>(
    ({ id, ...body }) => api.patch<AdminStage[]>(`/stages/${id}/color`, body),
    t("toast.stageUpdated"),
  );
}

export function useSetStageActive() {
  const t = useT();
  return useStageMutation<{ id: string; active: boolean; version: number }>(
    ({ id, ...body }) => api.patch<AdminStage[]>(`/stages/${id}/active`, body),
    t("toast.stageUpdated"),
  );
}

export function useReorderStages() {
  const t = useT();
  return useStageMutation<ReorderStagesInput>(
    (input) => api.put<AdminStage[]>("/stages/order", input),
    t("toast.orderChanged"),
  );
}


export function useSettings() {
  return useQuery({ queryKey: SETTINGS, queryFn: () => api.get<Setting[]>("/settings") });
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string; value: string; version: number }) =>
      api.patch<Setting[]>(`/settings/${key}`, body),
    onSuccess: async (settings) => {
      queryClient.setQueryData(SETTINGS, settings);
      // The stalled threshold is read inside the derivation view, so every board on screen
      // is now showing stalled flags computed against the old value.
      await queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      reportMutationSuccess(t("toast.settingSaved"));
    },
    onError: (error: unknown) => reportMutationError(error),
  });
}
